import { useRef, useCallback } from 'react';
import { Live2DModel } from 'pixi-live2d-display/cubism4';
import { Live2DModelExtended } from '../types/live2d';

export function useMouthAnimation() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startMouthAnimation = useCallback((model: Live2DModel | null) => {
    if (!model) return;
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const extendedModel = model as Live2DModelExtended;
    const core = extendedModel.internalModel?.coreModel;
    if (!core) return;

    const setParam = (id: string, value: number) => {
      try {
        core.setParameterValueById(id, value);
      } catch (e) {
        // Ignore missing parameter errors
      }
    };

    const mouthStates = [0, 0.5, 1.0];
    
    intervalRef.current = setInterval(() => {
      try {
        const currentState = Math.floor(Math.random() * mouthStates.length);
        const openValue = mouthStates[currentState];
        setParam('ParamMouthOpenY', openValue);
      } catch (e) {
        // Ignore errors
      }
    }, 100);
  }, []);

  const stopMouthAnimation = useCallback((model: Live2DModel | null) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (model) {
      try {
        const extendedModel = model as Live2DModelExtended;
        const core = extendedModel.internalModel?.coreModel;
        if (core) {
          core.setParameterValueById('ParamMouthOpenY', 0);
        }
      } catch (e) {
        // Ignore errors
      }
    }
  }, []);

  const animateMouthForDuration = useCallback((model: Live2DModel | null, text: string) => {
    if (!text || typeof text !== 'string' || !model) return;

    const baseSpeed = 100;
    const minDuration = 1000;
    const maxDuration = 3000;

    const mouthDuration = Math.min(
      Math.max(text.length * baseSpeed, minDuration),
      maxDuration
    );

    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
    }

    startMouthAnimation(model);

    stopTimerRef.current = setTimeout(() => {
      stopMouthAnimation(model);
      stopTimerRef.current = null;
    }, mouthDuration);
  }, [startMouthAnimation, stopMouthAnimation]);

  const animateMouthForAudio = useCallback((model: Live2DModel | null, audioDuration: number = 2000) => {
    if (!model) return;

    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
    }

    startMouthAnimation(model);

    stopTimerRef.current = setTimeout(() => {
      stopMouthAnimation(model);
      stopTimerRef.current = null;
    }, audioDuration);
  }, [startMouthAnimation, stopMouthAnimation]);

  return {
    startMouthAnimation,
    stopMouthAnimation,
    animateMouthForDuration,
    animateMouthForAudio,
  };
}